/**
 * The conversation, read out of the log.
 *
 * In the human conditions talk is audio: one `.webm` per seat, anchored on
 * `SYNC_BEEP` and driven by the transport in `file.ts`. A seat with no
 * microphone has no such file, so its half of the conversation is on the tape
 * as `UTTERANCE` events — and that is the whole of the AI-AI replay story. No
 * second renderer, no second fetch, nothing to drift, because there is no audio
 * track for it to drift against.
 *
 * These are pure functions over the parsed frames rather than a fold, and that
 * is deliberate. `EventFeed` accumulates as frames are pushed into it, and a
 * seek starts from the last keyframe at or before the target — so a feed-based
 * transcript would lose everything said before whichever keyframe the scrub bar
 * landed after. The frames are all in memory already; reading the whole
 * conversation from them and filtering by the playhead is both simpler and
 * correct under scrubbing.
 */

import type { EventFrame, PlayerId } from '@overcooked/shared'
import { TICK_MS } from '@overcooked/shared'

export interface Utterance {
  /** When it was said, on the only clock the log trusts. */
  tick: number
  playerId: PlayerId
  text: string
  /** How it left the speaker: typed into the tape, or spoken aloud. */
  channel: 'text' | 'tts'
}

/**
 * Every line anybody said, in tick order.
 *
 * Sorted rather than assumed sorted: `indexFrames` orders the frames it plays
 * back and this reads the raw list, so the two cannot disagree about a log
 * whose lines arrived out of order.
 */
export function collectUtterances(frames: EventFrame[]): Utterance[] {
  const said: Utterance[] = []
  for (const frame of frames) {
    for (const event of frame.events) {
      if (event.type !== 'UTTERANCE') continue
      said.push({
        tick: event.tick,
        playerId: event.playerId,
        text: event.text,
        channel: event.channel,
      })
    }
  }
  return said.sort((a, b) => a.tick - b.tick)
}

/** `m:ss` on the round's own clock, the same shape the transport shows. */
export function stamp(tick: number): string {
  const seconds = Math.max(0, Math.round((tick * TICK_MS) / 1000))
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}
